import { worldStorageKey } from '@/world';

export const KEY_ACTIONS = [
  { id: 'up', label: 'Move up', defaults: ['KeyW', 'ArrowUp'], code: 'KeyW' },
  { id: 'down', label: 'Move down', defaults: ['KeyS', 'ArrowDown'], code: 'KeyS' },
  { id: 'left', label: 'Move left', defaults: ['KeyA', 'ArrowLeft'], code: 'KeyA' },
  { id: 'right', label: 'Move right', defaults: ['KeyD', 'ArrowRight'], code: 'KeyD' },
  { id: 'attack', label: 'Attack', defaults: ['Space', 'ControlLeft', 'ControlRight'], code: 'Space' },
  { id: 'sit', label: 'Sit / stand', defaults: ['KeyX'], code: 'KeyX' },
  { id: 'summon', label: 'Summon companion', defaults: ['KeyF'], code: 'KeyF' },
  { id: 'companionAttack', label: 'Companion: attack nearby enemies', defaults: ['KeyG'], code: 'KeyG' },
  { id: 'target', label: 'Next enemy', defaults: ['KeyQ'], code: 'KeyQ' },
  { id: 'inventory', label: 'Inventory', defaults: ['KeyE'], code: 'KeyE' },
  { id: 'character', label: 'Character', defaults: ['KeyC'], code: 'KeyC' },
  { id: 'spells', label: 'Spells', defaults: ['KeyP'], code: 'KeyP' },
  { id: 'minimap', label: 'Minimap', defaults: ['Tab'], code: 'Tab' },
  { id: 'refresh', label: 'Refresh map', defaults: ['KeyR'], code: 'KeyR' },
  { id: 'pk', label: 'Toggle player combat', defaults: ['ShiftLeft', 'ShiftRight'], code: 'ShiftLeft' },
  ...Array.from({ length: 18 }, (_, i) => ({ id: `hotbar${i}`, label: `Hotbar ${i < 9 ? 1 : 2}, slot ${i % 9 + 1}`,
    defaults: [`${i >= 9 ? 'Shift+' : ''}Digit${i % 9 + 1}`], code: `Digit${i % 9 + 1}` })),
];
type Bindings = Record<string, string[]>;
let cached: Bindings | null = null;
const listeners = new Set<() => void>();
export function keyBindings(): Bindings {
  if (cached) return cached;
  const defaults = Object.fromEntries(KEY_ACTIONS.map(a => [a.id, [...a.defaults]]));
  try {
    const saved = JSON.parse(localStorage.getItem(worldStorageKey('keybindings')) ?? 'null');
    const savedActions = new Set<string>();
    if (saved && typeof saved === 'object') for (const a of KEY_ACTIONS) {
      if (Array.isArray(saved[a.id]) && saved[a.id].every((v: unknown) => typeof v === 'string' && /^(Shift\+)?(?:Key[A-Z]|Digit[0-9]|Arrow(?:Up|Down|Left|Right)|Space|Tab|ShiftLeft|ShiftRight|ControlLeft|ControlRight|Numpad(?:[0-9]|Decimal)|F[1-9]|F1[0-2]|BracketLeft|BracketRight|Semicolon|Quote|Comma|Period|Slash|Backquote|Minus|Equal)$/.test(v))) {defaults[a.id] = saved[a.id];savedActions.add(a.id);}
    }
    // New feature defaults must never steal a player's existing custom key.
    const reserved = new Set([...savedActions].flatMap(id => defaults[id]));
    for (const a of KEY_ACTIONS) if (!savedActions.has(a.id)) defaults[a.id] = defaults[a.id].filter(key => !reserved.has(key));
  } catch { /* Corrupt preferences never prevent login. */ }
  return cached = defaults;
}
export function keyChord(e: Pick<KeyboardEvent, 'code' | 'shiftKey'>): string {
  return `${e.shiftKey && !e.code.startsWith('Shift') && !e.code.startsWith('Control') ? 'Shift+' : ''}${e.code}`;
}
export function keyAction(e: Pick<KeyboardEvent, 'code' | 'shiftKey'>): string | null {
  const chord = keyChord(e), bindings = keyBindings();
  return KEY_ACTIONS.find(a => bindings[a.id].includes(chord))?.id ??
    (e.shiftKey && !/^Digit/.test(e.code) ? KEY_ACTIONS.find(a => bindings[a.id].includes(e.code))?.id ?? null : null);
}
export function keyLabel(chord: string): string {
  return chord.replace(/Key|Digit/g, '').replace(/Control(?:Left|Right)/g, 'Ctrl').replace(/Shift(?:Left|Right)/g, 'Shift').replace('Numpad', 'Num ');
}
export function actionLabel(id: string): string {
  return keyBindings()[id]?.map(keyLabel).filter((v, i, all) => all.indexOf(v) === i).join(' / ') || 'Unassigned';
}
export function setKeyBinding(id: string, chord: string | null): void {
  const bindings = keyBindings();
  if (!KEY_ACTIONS.some(a => a.id === id)) return;
  if (chord) for (const a of KEY_ACTIONS) bindings[a.id] = bindings[a.id].filter(c => c !== chord);
  bindings[id] = chord ? [chord] : [];
  save();
}
function save(): void {
  try { localStorage.setItem(worldStorageKey('keybindings'), JSON.stringify(cached)); } catch {}
  for (const cb of listeners) cb();
}
export function resetKeyBindings(): void { cached = null; try {localStorage.removeItem(worldStorageKey('keybindings'));} catch {} keyBindings(); for (const cb of listeners) cb(); }
export function subscribeKeyBindings(cb: () => void): () => void { listeners.add(cb); return () => {listeners.delete(cb);}; }
export function capturingKey(e: Event): boolean { return e.target instanceof Element && !!e.target.closest('[data-keybind-capture]'); }
